<!-- ─── Loading ───────────────────────────────────────────────────────────── -->
<div *ngIf="loading" class="mts-loading d-flex align-items-center justify-content-center">
  <i class="material-icons mts-spinner">sync</i>
  <span class="ms-2" style="color:#667085;font-size:14px">Loading available times…</span>
</div>

<!-- ─── Error Banner ──────────────────────────────────────────────────────── -->
<div *ngIf="error && !loading" class="mts-error-banner">
  <i class="material-icons" style="font-size:15px;vertical-align:middle;margin-right:6px">error_outline</i>
  {{ error }}
</div>

<!-- ─── Confirmed state (full-width centered, no calendar) ───────────────── -->
<div *ngIf="!loading && view === 'confirmed'" class="mts-confirmed-wrapper">
  <div class="mts-confirmed">
    <div class="mts-confirmed-icon">
      <i class="material-icons" style="font-size:36px;color:#fff">event_available</i>
    </div>
    <div class="mts-confirmed-title">Booking confirmed!</div>
    <div *ngIf="confirmation" class="mts-confirmed-details">
      <div class="mts-confirmed-date">{{ confirmation.displayDate }}</div>
      <div class="mts-confirmed-time">{{ confirmation.displayTime }}</div>
      <div class="mts-confirmed-host">with {{ confirmation.hostName }}</div>
    </div>
    <div class="mts-confirmed-note">
      A calendar invitation with the Zoom link has been sent to your email.
    </div>
  </div>
</div>

<!-- ══════════════════════════════════════════════════════════════════════════
     Main two-column layout (hidden once confirmed)
     ══════════════════════════════════════════════════════════════════════════ -->
<div *ngIf="!loading && view !== 'confirmed'" class="mts-layout">

  <!-- ─── Left Panel: Calendar ────────────────────────────────────────────── -->
  <div class="mts-left">

    <!-- Host (centered avatar + name) -->
    <div class="mts-host">
      <img src="assets/images/taxplaniq-logo.webp" alt="TaxPlanIQ" class="mts-avatar" style="object-fit:cover;">
      <div class="mts-host-title">Meet with Onboarding</div>
    </div>

    <!-- Month nav -->
    <div class="mts-month-nav">
      <button class="mts-nav-btn" (click)="prevMonth()" [disabled]="!canGoPrev">
        <i class="material-icons">chevron_left</i>
      </button>
      <span class="mts-month-label">{{ currentMonthLabel }}</span>
      <button class="mts-nav-btn" (click)="nextMonth()" [disabled]="!canGoNext">
        <i class="material-icons">chevron_right</i>
      </button>
    </div>

    <!-- Calendar grid -->
    <div class="mts-calendar">
      <div class="mts-day-header" *ngFor="let d of DAY_LABELS">{{ d }}</div>
      <button
        *ngFor="let day of calendarDays"
        class="mts-day"
        [class.mts-day--other-month]="day.isPrevMonth"
        [class.mts-day--today]="day.isToday"
        [class.mts-day--available]="day.hasSlots"
        [class.mts-day--selected]="selectedDate && day.date.toDateString() === selectedDate.toDateString()"
        [class.mts-day--disabled]="day.isPast || !day.hasSlots"
        [disabled]="day.isPast || !day.hasSlots"
        (click)="selectDate(day)">
        {{ day.date.getDate() }}
      </button>
    </div>

  </div><!-- /mts-left -->

  <!-- ─── Right Panel ──────────────────────────────────────────────────────── -->
  <div class="mts-right">

    <!-- ── Normal state: metadata + slots ── -->
    <ng-container *ngIf="view !== 'confirmed'">

      <!-- Meeting location -->
      <div class="mts-info-block">
        <div class="mts-info-label">Meeting location</div>
        <div class="mts-info-value">
          <i class="material-icons mts-pin-icon">location_on</i>Zoom
        </div>
      </div>

      <!-- Meeting duration -->
      <div class="mts-info-block">
        <div class="mts-info-label">{{ durations.length === 1 ? 'Meeting duration' : 'How long do you need?' }}</div>

        <!-- Single duration: non-interactive display pill -->
        <div *ngIf="durations.length === 1" class="mts-duration-display">
          {{ durations[0].label }}
        </div>

        <!-- Multiple durations: interactive toggle pills (wrap on overflow) -->
        <div *ngIf="durations.length > 1" class="mts-duration-options">
          <button
            *ngFor="let d of durations"
            class="mts-duration-btn"
            [class.mts-duration-btn--selected]="d.ms === selectedDuration"
            (click)="selectDuration(d.ms)">
            {{ d.label }}
          </button>
        </div>
      </div>

      <!-- Time slots -->
      <div class="mts-time-question">What time works best?</div>

      <div class="mts-time-for" *ngIf="selectedDate">
        Showing times for <strong>{{ formatSelectedDate() }}</strong>
      </div>
      <div class="mts-time-for mts-time-for--empty" *ngIf="!selectedDate">
        Select a date from the calendar
      </div>

      <!-- Timezone selector -->
      <div class="mts-tz-wrap">
        <div class="mts-tz-selector" (click)="tzDropdownOpen = !tzDropdownOpen">
          <i class="material-icons mts-tz-icon">public</i>
          <span class="mts-tz-label">{{ tzDisplay(timezone) }}</span>
          <i class="material-icons mts-tz-caret">{{ tzDropdownOpen ? 'expand_less' : 'expand_more' }}</i>
        </div>
        <div class="mts-tz-backdrop" *ngIf="tzDropdownOpen" (click)="tzDropdownOpen = false"></div>
        <div class="mts-tz-dropdown" *ngIf="tzDropdownOpen">
          <div class="mts-tz-search-row">
            <i class="material-icons mts-tz-search-icon">search</i>
            <input class="mts-tz-input" placeholder="Search" #tzRef
                   [value]="tzSearch" (input)="tzSearch = tzRef.value" autofocus>
          </div>
          <div class="mts-tz-list">
            <ng-container *ngFor="let group of filteredGroups">
              <div class="mts-tz-group-label">{{ group.group }}</div>
              <button class="mts-tz-option"
                      *ngFor="let tz of group.zones"
                      [class.mts-tz-option--active]="tz === timezone"
                      (click)="selectTimezone(tz)">
                {{ tzDisplay(tz) }}
              </button>
            </ng-container>
            <div class="mts-tz-empty" *ngIf="filteredGroups.length === 0">No results</div>
          </div>
        </div>
      </div>

      <!-- Scrollable slot area -->
      <div class="mts-slots-scroll-wrapper">
        <div class="mts-slot-list" *ngIf="slotsForDate.length > 0">
          <button
            *ngFor="let slot of slotsForDate"
            class="mts-slot-btn"
            [class.mts-slot-btn--selected]="selectedSlot === slot"
            (click)="selectSlot(slot)">
            {{ formatSlotTime(slot) }}
          </button>
        </div>

        <div class="mts-no-slots" *ngIf="selectedDate && slotsForDate.length === 0">
          No available times for this date.
        </div>
      </div>

      <!-- Confirm button (pinned at bottom, appears after selecting a slot) -->
      <div class="mts-confirm-row" *ngIf="selectedSlot">
        <button class="mts-confirm-btn" (click)="confirmBooking()" [disabled]="booking">
          <i *ngIf="booking" class="material-icons mts-spinner" style="font-size:14px;vertical-align:middle">sync</i>
          {{ booking ? 'Booking…' : 'Confirm booking' }}
        </button>
      </div>

    </ng-container><!-- /normal state -->

  </div><!-- /mts-right -->
</div><!-- /mts-layout (hidden when confirmed) -->
